import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { can, canAny } from './hooks/usePermissions';
import { GoogleMapsProvider } from './context/GoogleMapsContext';
import Layout from './components/common/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import UserDetails from './pages/UserDetails';
import Drivers from './pages/Drivers';
import DriverDetails from './pages/DriverDetails';
import Rides from './pages/Rides';
import RideDetails from './pages/RideDetails';
import Payments from './pages/Payments';
import Payouts from './pages/Payouts';
import Payroll from './pages/Payroll';
import Ratings from './pages/Ratings';
import Settings from './pages/Settings';
import AuditLog from './pages/AuditLog';
import ServiceAreas from './pages/ServiceAreas';
import Coupons from './pages/Coupons';
import Referrals from './pages/Referrals';
import Broadcasts from './pages/Broadcasts';
import Ads from './pages/Ads';
import Admins from './pages/Admins';
import StandingOrders from './pages/StandingOrders';
import StandingOrderDetails from './pages/StandingOrderDetails';

const RequirePermission = ({ permission, permissions, children }) => {
  const { admin } = useAuth();
  const allowed = permissions
    ? canAny(admin, permissions)
    : can(admin, permission);
  if (!allowed) return <Navigate to="/" replace />;
  return children;
};

const ProtectedRoute = ({ children }) => {
  const { admin, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (!admin) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <GoogleMapsProvider>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/users" element={<Users />} />
                <Route path="/users/:id" element={<UserDetails />} />
                <Route path="/drivers" element={<Drivers />} />
                <Route path="/drivers/:id" element={<DriverDetails />} />
                <Route path="/rides" element={<Rides />} />
                <Route path="/rides/:id" element={<RideDetails />} />
                <Route path="/payments" element={<Payments />} />
                <Route path="/payouts" element={<RequirePermission permissions={['manage_payments', 'manage_payouts']}><Payouts /></RequirePermission>} />
                <Route path="/payroll" element={<RequirePermission permissions={['manage_payments', 'manage_payouts']}><Payroll /></RequirePermission>} />
                <Route path="/ratings" element={<RequirePermission permissions={['manage_drivers', 'manage_users']}><Ratings /></RequirePermission>} />
                <Route path="/settings" element={<Navigate to="/settings/general" replace />} />
                <Route path="/settings/:section" element={<Settings />} />
                <Route path="/service-areas" element={<ServiceAreas />} />
                <Route path="/coupons" element={<Coupons />} />
                <Route path="/referrals" element={<Referrals />} />
                <Route path="/broadcasts" element={<Broadcasts />} />
                <Route path="/ads" element={<Ads />} />
                <Route path="/admins" element={<Admins />} />
                <Route path="/audit-logs" element={<AuditLog />} />
                <Route path="/standing-orders" element={<RequirePermission permission="manage_rides"><StandingOrders /></RequirePermission>} />
                <Route path="/standing-orders/:id" element={<RequirePermission permission="manage_rides"><StandingOrderDetails /></RequirePermission>} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
    </GoogleMapsProvider>
  );
}

export default App;
